import { useMemo, useState, useTransition } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Layout } from '../../components/Layout';
import {
  selectActiveSubscription,
  selectSubscriptionSummaries,
  setActiveSubscriptionId,
} from '../../store/slices/subscription';
import { SubscriptionSummary } from '../../types/subscription';
import { selectOverviewSubSort } from '../../store/slices/ui';
import { selectIsDemo } from '../../store/slices/demo';
import useDemoClippedActiveSubscription from '../../hooks/useDemoClippedActiveSubscription';
import {
  sortSubscriptions,
  SortOption,
} from '../Overview/Content/FilterContext';
import Content from './Content';

export enum SubscriptionTabs {
  Overview = 'OVERVIEW',
  Transactions = 'TRANSACTIONS',
  Calculations = 'CALCULATIONS',
  Manage = 'MANAGE',
}

/**
 * Subscription detail screen (route /sub): masthead header + sidebar (sections
 * nav, subscription list) and the Overview / Transactions / Calculations /
 * Manage tabs. The active subscription's detail is loaded by Content; this shell
 * reads it from the store.
 */
const Subscription = () => {
  const dispatch = useDispatch();

  const rawSummaries = useSelector(selectSubscriptionSummaries);
  const rawActive = useSelector(selectActiveSubscription);
  const subSort = useSelector(selectOverviewSubSort) as SortOption;
  const isDemo = useSelector(selectIsDemo);
  // Side menu lists every visible sub (excluded ones hidden in demo); the
  // active subscription is the rebased version of whatever's selected, so the
  // header / chart / transactions all read the windowed values. The hook is
  // a no-op outside demo mode.
  const { subscriptionSummaries, activeSubscription } =
    useDemoClippedActiveSubscription({ rawActive, rawSummaries });

  const [activeTab, setActiveTab] = useState(SubscriptionTabs.Overview);
  // Switch tabs as a non-urgent transition so the heavy Calculations render
  // never blocks/freezes the UI on click.
  const [, startTransition] = useTransition();

  const subscriptionsByCluster = useMemo(() => {
    return subscriptionSummaries.reduce<
      Record<string, Array<SubscriptionSummary>>
    >((acc, item) => {
      (acc[item.clusterId] = acc[item.clusterId] || []).push(item);
      return acc;
    }, {});
  }, [subscriptionSummaries]);

  const handleSubscriptionClick = (subscriptionId: number) => {
    dispatch(setActiveSubscriptionId(subscriptionId));
  };

  const hasPositiveProfit = !activeSubscription
    ? undefined
    : (activeSubscription.currentProfit || 0) >= 0;

  const sections = useMemo(
    () =>
      [
        SubscriptionTabs.Overview,
        SubscriptionTabs.Transactions,
        SubscriptionTabs.Calculations,
        // Manage owns the write/mutate surface (change pair, adjust balance,
        // pause, remove). Demo is strictly read-only, so omit the tab entirely
        // rather than rendering a disabled one.
        ...(isDemo ? [] : [SubscriptionTabs.Manage]),
      ].map((tab) => ({
        tab,
        label: tab.charAt(0) + tab.slice(1).toLowerCase(),
        isActive: activeTab === tab,
        onClick: () => startTransition(() => setActiveTab(tab)),
      })),
    [activeTab, isDemo],
  );

  return (
    <Layout hasPositiveProfit={hasPositiveProfit}>
      {!!activeSubscription ? (
        <Layout.SideMenu hasPositiveProfit={hasPositiveProfit}>
          <Layout.SideMenuSection>
            <Layout.SideMenuTitle title="Sections" count={sections.length} />
            <>
              {sections.map((section) => (
                <Layout.SideMenuItem
                  key={section.label}
                  id={section.label}
                  label={section.label}
                  isActive={section.isActive}
                  onClick={section.onClick}
                />
              ))}
            </>
          </Layout.SideMenuSection>

          <Layout.SideMenuSection>
            <Layout.SideMenuTitle
              title="Subscriptions"
              count={Object.values(subscriptionsByCluster).reduce(
                (total, subs) => total + subs.length,
                0,
              )}
              toggle
            />
            <>
              {Object.entries(subscriptionsByCluster).map(
                ([clusterId, subscriptions]) => (
                  <div key={clusterId}>
                    <Layout.SideMenuSubTitle
                      title={clusterId}
                      count={subscriptions.length}
                    />
                    {sortSubscriptions(subscriptions, subSort).map((sub) => (
                      <Layout.SideMenuItem
                        key={sub.id}
                        id={sub.id}
                        label={sub.botFriendlyName}
                        value={sub.totalBalanceInStable}
                        delta={sub.currentProfit}
                        deltaTotal={sub.profits?.total.stable.relative}
                        isActive={activeSubscription?.id === sub.id}
                        onClick={handleSubscriptionClick}
                      />
                    ))}
                  </div>
                ),
              )}
            </>
          </Layout.SideMenuSection>
        </Layout.SideMenu>
      ) : undefined}

      <Layout.Content>
        <Content activeTab={activeTab} subscription={activeSubscription} />
      </Layout.Content>
    </Layout>
  );
};

export default Subscription;
